import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send, 
  Building, 
  TrendingUp,
  CheckCircle,
  Users,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Search,
  Eye,
  EyeOff,
  X,
  Lock
} from 'lucide-react';
import { collection, query, onSnapshot, doc, setDoc, getDoc } from 'firebase/firestore';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  updateProfile,
  User
} from 'firebase/auth';
import { auth, db } from '../firebase';
import { saveQuoteRequest, QuoteRequest } from '../services/quoteService';
import { saveProductRequest } from '../services/customerService';
import { useToast } from '../components/Toast';
import { sendQuoteRequestEmail } from '../services/emailService';
import { logger } from '../utils/logger';

const CONSULTATION_OPTION = 'Not Sure - Need Consultation';

const industryLabel = (industry: unknown): string => {
  if (Array.isArray(industry)) return industry.filter(Boolean).join(' · ');
  return typeof industry === 'string' ? industry : '';
};

const ProductThumb: React.FC<{ product: any; className?: string }> = ({ product, className = 'h-14 w-14' }) => {
  const src = product?.image;
  if (typeof src === 'string' && (src.startsWith('data:image/') || src.startsWith('/'))) {
    return (
      <img
        src={src}
        alt={product.name}
        className={`${className} object-contain rounded-lg bg-gray-50`}
      />
    );
  }
  return (
    <div className={`${className} flex items-center justify-center rounded-lg bg-gray-50 text-2xl`}>
      {src || '📦'}
    </div>
  );
};

const Contact: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    
    // Business Information
    businessName: '',
    businessAddress: '',
    city: '',
    state: '',
    zipCode: '',
    
    // Business Details
    businessType: '',
    yearsInBusiness: '',
    monthlyVolume: '',
    annualRevenue: '',
    currentProcessor: '',
    
    // System Needs
    systemType: '',
    additionalNeeds: '',
    timeline: '',
    
    // Additional Info
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const pickerRef = useRef<HTMLDivElement>(null);
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [authMode, setAuthMode] = useState<'register' | 'login'>('register');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const googleProvider = useRef(new GoogleAuthProvider()).current;
  const { success, error: showError, ToastContainer } = useToast();
  const addressInputRef = React.useRef<HTMLInputElement>(null);
  const autocompleteRef = React.useRef<google.maps.places.Autocomplete | null>(null);

  const businessTypes = [
    'Retail Store',
    'Restaurant/Food Service',
    'Healthcare/Medical',
    'Professional Services',
    'E-commerce/Online',
    'Manufacturing',
    'Construction',
    'Automotive',
    'Beauty/Salon',
    'Fitness/Wellness',
    'Real Estate',
    'Education',
    'Non-profit',
    'Other'
  ];

  // Initialize Google Places Autocomplete
  useEffect(() => {
    const initAutocomplete = () => {
      if (addressInputRef.current && typeof google !== 'undefined' && google.maps && google.maps.places) {
        try {
          // Clean up existing autocomplete if it exists
          if (autocompleteRef.current && typeof google !== 'undefined' && google.maps && google.maps.event) {
            google.maps.event.clearInstanceListeners(autocompleteRef.current);
          }

          const autocomplete = new google.maps.places.Autocomplete(addressInputRef.current, {
            types: ['address'],
            componentRestrictions: { country: 'us' }
          });

          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            
            if (place.address_components) {
              let streetNumber = '';
              let route = '';
              let city = '';
              let state = '';
              let zipCode = '';

              place.address_components.forEach((component) => {
                const types = component.types;
                
                if (types.includes('street_number')) {
                  streetNumber = component.long_name;
                }
                if (types.includes('route')) {
                  route = component.long_name;
                }
                if (types.includes('locality')) {
                  city = component.long_name;
                }
                if (types.includes('administrative_area_level_1')) {
                  state = component.short_name;
                }
                if (types.includes('postal_code')) {
                  zipCode = component.long_name;
                }
              });

              const fullAddress = place.formatted_address || `${streetNumber} ${route}`.trim();

              setFormData(prev => ({
                ...prev,
                businessAddress: fullAddress,
                city: city || prev.city,
                state: state || prev.state,
                zipCode: zipCode || prev.zipCode
              }));
            }
          });

          autocompleteRef.current = autocomplete;
        } catch (error) {
          logger.error('Error initializing Google Places Autocomplete:', error);
        }
      }
    };

    // Only initialize when we're on step 2 (Business Information step)
    if (currentStep === 2) {
      // Wait for Google Maps API to load
      if (typeof google !== 'undefined' && google.maps && google.maps.places) {
        // Small delay to ensure the input is in the DOM
        setTimeout(() => {
          initAutocomplete();
        }, 100);
      } else {
        // If not loaded yet, wait for it
        const checkGoogle = setInterval(() => {
          if (typeof google !== 'undefined' && google.maps && google.maps.places) {
            setTimeout(() => {
              initAutocomplete();
            }, 100);
            clearInterval(checkGoogle);
          }
        }, 100);

        // Cleanup interval after 10 seconds
        setTimeout(() => clearInterval(checkGoogle), 10000);
      }
    }

    return () => {
      if (autocompleteRef.current && typeof google !== 'undefined' && google.maps && google.maps.event) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
      }
    };
  }, [currentStep]);

  // Load products from Firestore for system dropdown
  useEffect(() => {
    logger.log('🎯 Contact Page: Setting up real-time listener for products');
    setIsLoadingProducts(true);

    const productsQuery = query(collection(db, 'products'));

    const unsubscribeProducts = onSnapshot(productsQuery, (snapshot) => {
      try {
        logger.log('🔄 Contact Page: Real-time update received!');
        logger.log('📊 Products snapshot:', snapshot.docs.length, 'documents');

        const productsData = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        }));

        logger.log('📦 Updated products catalog:', productsData.length, 'products');
        setProducts(productsData);
      } catch (error) {
        logger.error('❌ Error processing products update:', error);
      } finally {
        setIsLoadingProducts(false);
      }
    }, (error) => {
      logger.error('❌ Products listener error:', error);
      setIsLoadingProducts(false);
    });

    return () => {
      logger.log('🧹 Contact Page: Cleaning up real-time listener');
      unsubscribeProducts();
    };
  }, []);

  useEffect(() => {
    const requested = searchParams.get('product');
    if (!requested || products.length === 0) return;
    const match = products.find((product: any) =>
      product.id === requested ||
      product.name?.toLowerCase() === decodeURIComponent(requested).toLowerCase()
    );
    if (match) {
      setFormData(prev => (
        prev.systemType === match.name ? prev : { ...prev, systemType: match.name }
      ));
    }
  }, [searchParams, products]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedCatalogProduct = products.find((product: any) => product.name === formData.systemType);
  const filteredPickerProducts = products.filter((product: any) => {
    if (!pickerQuery.trim()) return true;
    const haystack = `${product.name} ${industryLabel(product.industry)} ${product.price}`.toLowerCase();
    return haystack.includes(pickerQuery.trim().toLowerCase());
  });

  const selectSystem = (value: string, productId?: string) => {
    setFormData(prev => ({ ...prev, systemType: value }));
    setPickerOpen(false);
    setPickerQuery('');
    if (productId) {
      navigate(`/contact?product=${encodeURIComponent(productId)}`, { replace: true });
    } else {
      navigate('/contact', { replace: true });
    }
  };

  const timelines = [
    'Immediately',
    'Within 1 month',
    'Within 3 months',
    'Within 6 months',
    'Just exploring options'
  ];

  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format based on length
    if (phoneNumber.length <= 3) {
      return phoneNumber.length > 0 ? `(${phoneNumber}` : phoneNumber;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)} ${phoneNumber.slice(6, 10)}`;
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Format phone number if it's the phone field
    if (name === 'phone') {
      const formatted = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: formatted
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Step validation functions
  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(formData.firstName && formData.lastName && formData.email && formData.phone);
      case 2:
        return !!(formData.businessName && formData.businessAddress && formData.city && formData.state && formData.zipCode);
      case 3:
        return !!(formData.businessType && formData.yearsInBusiness && formData.monthlyVolume && formData.timeline);
      case 4:
        return !!(formData.systemType);
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (validateStep(currentStep) && currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      showError('Please fill in all required fields before continuing.');
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const progressPercentage = (currentStep / totalSteps) * 100;

  const saveCustomerProfile = async (user: User, isNewAccount: boolean) => {
    const userRef = doc(db, 'users', user.uid);
    const existing = await getDoc(userRef);
    const alreadyExists = existing.exists();
    await setDoc(userRef, {
      firstName: formData.firstName || user.displayName?.split(' ')[0] || '',
      lastName: formData.lastName || user.displayName?.split(' ').slice(1).join(' ') || '',
      email: user.email || formData.email,
      phone: formData.phone || '',
      businessName: formData.businessName,
      businessAddress: formData.businessAddress,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode,
      businessType: formData.businessType,
      yearsInBusiness: formData.yearsInBusiness,
      monthlyVolume: formData.monthlyVolume,
      currentProcessor: formData.currentProcessor,
      ...(alreadyExists || !isNewAccount
        ? { updatedAt: new Date() }
        : { userType: 'customer', role: 'customer', createdAt: new Date(), provider: user.providerData[0]?.providerId || 'email' })
    }, { merge: true });
  };

  const finalizeQuote = async (user: User, isNewAccount: boolean) => {
    await saveCustomerProfile(user, isNewAccount);

    const quoteData: Omit<QuoteRequest, 'id'> = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: user.email || formData.email,
        phone: formData.phone,
        businessName: formData.businessName,
        businessAddress: formData.businessAddress,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        businessType: formData.businessType,
        yearsInBusiness: formData.yearsInBusiness,
        monthlyVolume: formData.monthlyVolume,
        annualRevenue: formData.annualRevenue,
        currentProcessor: formData.currentProcessor,
        systemType: formData.systemType,
        additionalNeeds: formData.additionalNeeds,
        timeline: formData.timeline,
        message: formData.message,
        status: 'pending' as const,
        createdAt: new Date(),
        userId: user.uid,
        productId: selectedCatalogProduct?.id,
        productName: formData.systemType,
        customerEmail: user.email || formData.email,
        customerName: `${formData.firstName} ${formData.lastName}`.trim()
      };

      logger.log('💾 Attempting to save quote request to Firestore...', quoteData);
      const savedQuote = await saveQuoteRequest(quoteData);
      logger.log('✅ Quote request saved to Firestore successfully with ID:', savedQuote.id);

      await saveProductRequest({
        customerId: user.uid,
        customerEmail: user.email || formData.email,
        customerName: `${formData.firstName} ${formData.lastName}`.trim() || user.displayName || 'Customer',
        productId: selectedCatalogProduct?.id || 'consultation',
        productName: formData.systemType,
        requestType: 'product_request',
        description: [
          `Quote request for ${formData.systemType}`,
          formData.additionalNeeds ? `Needs: ${formData.additionalNeeds}` : '',
          formData.message ? `Notes: ${formData.message}` : ''
        ].filter(Boolean).join(' — '),
        urgency: 'normal',
        status: 'pending'
      });

    try {
      const emailParams = {
        from_name: `${formData.firstName} ${formData.lastName}` || 'N/A',
        from_email: formData.email || 'N/A',
        phone: formData.phone || 'N/A',
        business_name: formData.businessName || 'N/A',
        business_address: formData.businessAddress || 'N/A',
        city: formData.city || 'N/A',
        state: formData.state || 'N/A',
        zip_code: formData.zipCode || 'N/A',
        business_type: formData.businessType || 'N/A',
        years_in_business: formData.yearsInBusiness || 'N/A',
        monthly_volume: formData.monthlyVolume || 'N/A',
        annual_revenue: formData.annualRevenue || 'Not provided',
        current_processor: formData.currentProcessor || 'Not provided',
        system_type: formData.systemType || 'N/A',
        additional_needs: (formData.additionalNeeds || '').trim() || 'None',
        timeline: formData.timeline || 'N/A',
        message: (formData.message || '').trim() || 'None',
        debug_info: `Form submitted at ${new Date().toISOString()}`
      };
      await sendQuoteRequestEmail(emailParams);
    } catch (emailError) {
      logger.error('⚠️ EmailJS failed but quote request was saved:', emailError);
    }

    success('Request submitted', 'Your device request is now in your customer portal.');
    setShowAccountModal(false);
    navigate('/customer-dashboard');
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (currentStep < totalSteps) {
      nextStep();
      return;
    }
    if (!validateStep(1) || !validateStep(2) || !validateStep(3) || !validateStep(4)) {
      showError('Please complete all required fields before submitting.');
      return;
    }

    if (auth.currentUser) {
      setIsSubmitting(true);
      try {
        await finalizeQuote(auth.currentUser, false);
      } catch (error: any) {
        logger.error('❌ Error submitting quote request:', error);
        showError('Failed to submit quote request', error?.message || 'Please try again.');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    setAuthMode('register');
    setPassword('');
    setConfirmPassword('');
    setAuthError('');
    setShowAccountModal(true);
  };

  const handleCreateAccount = async () => {
    setAuthError('');
    if (password.length < 6) {
      setAuthError('Password must be at least 6 characters. Please try again');
      return;
    }
    if (password !== confirmPassword) {
      setAuthError('Passwords do not match. Please try again');
      return;
    }
    setIsSubmitting(true);
    try {
      const credential = await createUserWithEmailAndPassword(auth, formData.email.trim(), password);
      if (formData.firstName || formData.lastName) {
        await updateProfile(credential.user, {
          displayName: `${formData.firstName} ${formData.lastName}`.trim()
        });
      }
      await finalizeQuote(credential.user, true);
    } catch (error: any) {
      if (error?.code === 'auth/email-already-in-use') {
        setAuthMode('login');
        setAuthError('This email already has an account. Enter your password to sign in and add this device.');
      } else if (error?.code === 'auth/invalid-email') {
        setAuthError('Please enter a valid email address.');
      } else if (error?.code === 'auth/weak-password') {
        setAuthError('Password is too weak. Use at least 6 characters.');
      } else {
        setAuthError(error?.message || 'Could not create account.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAccountLogin = async () => {
    setAuthError('');
    if (!password) {
      setAuthError('Enter your password to continue.');
      return;
    }
    setIsSubmitting(true);
    try {
      const credential = await signInWithEmailAndPassword(auth, formData.email.trim(), password);
      await finalizeQuote(credential.user, false);
    } catch (error: any) {
      if (error?.code === 'auth/wrong-password' || error?.code === 'auth/invalid-credential') {
        setAuthError('Wrong password. Try again or continue with Google.');
      } else if (error?.code === 'auth/user-not-found') {
        setAuthMode('register');
        setAuthError('No account found. Create a password to register.');
      } else {
        setAuthError(error?.message || 'Could not sign in.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleAccount = async () => {
    setAuthError('');
    setIsSubmitting(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      await finalizeQuote(result.user, false);
    } catch (error: any) {
      if (error?.code !== 'auth/popup-closed-by-user') {
        setAuthError(error?.message || 'Google sign-in failed.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-6">
            We've received your information and will contact you within 24 hours with a personalized quote.
          </p>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="text-sm text-gray-800">
              <strong>Next Steps:</strong> Our team will review your requirements and prepare a customized solution for your business.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <ToastContainer />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Get Your Custom Quote</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Tell us about your business and we'll create a personalized payment solution that fits your needs.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Step {currentStep} of {totalSteps}</span>
            <span className="text-sm font-medium text-gray-700">{Math.round(progressPercentage)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className="bg-gradient-to-r from-gray-800 to-gray-900 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>

        {selectedCatalogProduct && (
          <div className="mb-6 flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <ProductThumb product={selectedCatalogProduct} className="h-16 w-16 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Quoting this system</p>
              <p className="text-base font-semibold text-gray-900 truncate">{selectedCatalogProduct.name}</p>
              <p className="text-sm text-gray-600">{selectedCatalogProduct.price}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-lg shadow-lg p-6 sm:p-8">
          <form
            onSubmit={handleSubmit}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              const tag = (event.target as HTMLElement).tagName;
              if (tag === 'TEXTAREA' || tag === 'BUTTON') return;
              event.preventDefault();
              if (currentStep < totalSteps) {
                nextStep();
              }
            }}
            className="space-y-6"
          >
            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
              <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <Users className="h-5 w-5 mr-2" />
                    Contact Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name *
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                        placeholder="John"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                        placeholder="Smith"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                        placeholder="john@business.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        maxLength={17}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                        placeholder="(551) 216 8218"
                      />
                    </div>
                  </div>
                </div>
            )}

            {/* Step 2: Business Information */}
            {currentStep === 2 && (
              <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <Building className="h-5 w-5 mr-2" />
                    Business Information
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Business Name *
                      </label>
                      <input
                        type="text"
                        name="businessName"
                        value={formData.businessName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                        placeholder="Your Business LLC"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Business Address *
                      </label>
                      <input
                        ref={addressInputRef}
                        type="text"
                        name="businessAddress"
                        value={formData.businessAddress}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                        placeholder="Start typing your address..."
                        autoComplete="off"
                      />
                      <p className="text-xs text-gray-500 mt-1">Start typing and select from suggestions</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          City *
                        </label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                          placeholder="New York"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          State *
                        </label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                          placeholder="NY"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          ZIP Code *
                        </label>
                        <input
                          type="text"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleInputChange}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                          placeholder="10001"
                        />
                      </div>
                    </div>
                  </div>
                </div>
            )}

            {/* Step 3: Business Details */}
            {currentStep === 3 && (
              <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <TrendingUp className="h-5 w-5 mr-2" />
                    Business Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type of Business *
                      </label>
                      <select
                        name="businessType"
                        value={formData.businessType}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                      >
                        <option value="">Select business type</option>
                        {businessTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Years in Business *
                      </label>
                      <select
                        name="yearsInBusiness"
                        value={formData.yearsInBusiness}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                      >
                        <option value="">Select years</option>
                        <option value="0-1">Less than 1 year</option>
                        <option value="1-2">1-2 years</option>
                        <option value="2-5">2-5 years</option>
                        <option value="5-10">5-10 years</option>
                        <option value="10+">10+ years</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Monthly Processing Volume *
                      </label>
                      <select
                        name="monthlyVolume"
                        value={formData.monthlyVolume}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                      >
                        <option value="">Select volume</option>
                        <option value="0-1000">$0 - $1,000</option>
                        <option value="1000-5000">$1,000 - $5,000</option>
                        <option value="5000-10000">$5,000 - $10,000</option>
                        <option value="10000-25000">$10,000 - $25,000</option>
                        <option value="25000-50000">$25,000 - $50,000</option>
                        <option value="50000+">$50,000+</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Annual Revenue
                      </label>
                      <select
                        name="annualRevenue"
                        value={formData.annualRevenue}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                      >
                        <option value="">Select revenue</option>
                        <option value="0-25000">$0 - $25,000</option>
                        <option value="25000-50000">$25,000 - $50,000</option>
                        <option value="50000-100000">$50,000 - $100,000</option>
                        <option value="100000-250000">$100,000 - $250,000</option>
                        <option value="250000-500000">$250,000 - $500,000</option>
                        <option value="500000+">$500,000+</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Payment Processor
                      </label>
                      <input
                        type="text"
                        name="currentProcessor"
                        value={formData.currentProcessor}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                        placeholder="Square, Stripe, etc."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Timeline *
                      </label>
                      <select
                        name="timeline"
                        value={formData.timeline}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                      >
                        <option value="">Select timeline</option>
                        {timelines.map(timeline => (
                          <option key={timeline} value={timeline}>{timeline}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
            )}

            {/* Step 4: System Needs */}
            {currentStep === 4 && (
              <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <CreditCard className="h-5 w-5 mr-2" />
                    System Requirements
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        What type of system do you need? *
                      </label>
                      <div className="relative" ref={pickerRef}>
                        <button
                          type="button"
                          onClick={() => !isLoadingProducts && setPickerOpen((open) => !open)}
                          disabled={isLoadingProducts}
                          className="w-full flex items-center gap-3 rounded-xl border border-gray-300 bg-white px-3 py-3 text-left shadow-sm hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 disabled:opacity-60"
                        >
                          {selectedCatalogProduct ? (
                            <>
                              <ProductThumb product={selectedCatalogProduct} className="h-12 w-12 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <p className="font-medium text-gray-900 truncate">{selectedCatalogProduct.name}</p>
                                <p className="text-sm text-gray-500 truncate">
                                  {selectedCatalogProduct.price}
                                  {industryLabel(selectedCatalogProduct.industry) ? ` · ${industryLabel(selectedCatalogProduct.industry)}` : ''}
                                </p>
                              </div>
                            </>
                          ) : formData.systemType === CONSULTATION_OPTION ? (
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-gray-900">Not sure — need consultation</p>
                              <p className="text-sm text-gray-500">We’ll help you pick the right system</p>
                            </div>
                          ) : (
                            <div className="min-w-0 flex-1">
                              <p className="text-gray-500">
                                {isLoadingProducts ? 'Loading products...' : 'Select a system'}
                              </p>
                            </div>
                          )}
                          <ChevronDown className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform ${pickerOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {pickerOpen && (
                          <div className="absolute left-0 right-0 z-50 mt-2 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl">
                            <div className="border-b border-gray-100 p-3">
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <input
                                  type="text"
                                  value={pickerQuery}
                                  onChange={(e) => setPickerQuery(e.target.value)}
                                  placeholder="Search systems..."
                                  className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
                                  autoFocus
                                />
                              </div>
                            </div>
                            <div className="max-h-72 overflow-y-auto py-1">
                              <button
                                type="button"
                                onClick={() => selectSystem(CONSULTATION_OPTION)}
                                className={`w-full px-3 py-3 text-left hover:bg-gray-50 ${
                                  formData.systemType === CONSULTATION_OPTION ? 'bg-gray-50' : ''
                                }`}
                              >
                                <p className="font-medium text-gray-900">Not sure — need consultation</p>
                                <p className="text-sm text-gray-500">No system selected yet</p>
                              </button>
                              {filteredPickerProducts.length === 0 ? (
                                <p className="px-3 py-4 text-sm text-gray-500">No systems match that search.</p>
                              ) : (
                                filteredPickerProducts.map((product: any) => {
                                  const selected = formData.systemType === product.name;
                                  return (
                                    <button
                                      key={product.id}
                                      type="button"
                                      onClick={() => selectSystem(product.name, product.id)}
                                      className={`w-full flex items-center gap-3 px-3 py-3 text-left hover:bg-gray-50 ${
                                        selected ? 'bg-gray-50' : ''
                                      }`}
                                    >
                                      <ProductThumb product={product} className="h-12 w-12 flex-shrink-0" />
                                      <div className="min-w-0 flex-1">
                                        <p className="font-medium text-gray-900 truncate">{product.name}</p>
                                        <p className="text-sm text-gray-500 truncate">
                                          {product.price}
                                          {industryLabel(product.industry) ? ` · ${industryLabel(product.industry)}` : ''}
                                        </p>
                                      </div>
                                      {selected && <CheckCircle className="h-4 w-4 text-gray-900 flex-shrink-0" />}
                                    </button>
                                  );
                                })
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Additional Requirements
                      </label>
                      <textarea
                        name="additionalNeeds"
                        value={formData.additionalNeeds}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                        placeholder="Tell us about any specific features or requirements..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Additional Information
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                        placeholder="Any other information that would help us provide a better quote..."
                      />
                    </div>
                  </div>
                </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={prevStep}
                    disabled={currentStep === 1}
                    className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 ${
                      currentStep === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <ChevronLeft className="h-5 w-5" />
                    Back
                  </button>

                  {currentStep < totalSteps ? (
                    <button
                      type="button"
                      onClick={nextStep}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg font-semibold hover:from-gray-900 hover:to-black transition-all duration-300"
                    >
                      Next
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleSubmit()}
                      disabled={isSubmitting}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg font-semibold hover:from-gray-900 hover:to-black transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="h-5 w-5" />
                          Get My Custom Quote
                        </>
                      )}
                    </button>
                  )}
                </div>
              </form>
          </div>
        </div>

      {showAccountModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !isSubmitting && setShowAccountModal(false)} aria-hidden />
          <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-xl ring-1 ring-black/5">
            <button
              type="button"
              onClick={() => !isSubmitting && setShowAccountModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <div className="mb-5">
              <h3 className="text-xl font-semibold text-gray-900">
                {authMode === 'login' ? 'Welcome back' : 'Create your customer account'}
              </h3>
              <p className="mt-1 text-sm text-gray-600">
                {authMode === 'login'
                  ? 'Sign in to add this device request to your portal.'
                  : 'Set a password or continue with Google. This keeps your quote on your customer page.'}
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
                <Mail className="h-4 w-4 text-gray-400" />
                <span className="truncate">{formData.email}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleGoogleAccount}
              disabled={isSubmitting}
              className="mb-4 w-full flex items-center justify-center px-4 py-2.5 border border-gray-300 rounded-lg bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-2 text-gray-500">or use a password</span>
              </div>
            </div>

            {authError && (
              <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {authError}
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-10 text-sm focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-900"
                    placeholder={authMode === 'login' ? 'Your password' : 'At least 6 characters'}
                    autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {authMode === 'register' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:border-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-900"
                    placeholder="Repeat password"
                    autoComplete="new-password"
                  />
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={authMode === 'login' ? handleAccountLogin : handleCreateAccount}
              disabled={isSubmitting}
              className="mt-5 w-full rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-black disabled:opacity-50"
            >
              {isSubmitting
                ? 'Saving your request...'
                : authMode === 'login'
                  ? 'Sign in and add request'
                  : 'Create account and submit'}
            </button>

            <p className="mt-4 text-center text-sm text-gray-600">
              {authMode === 'login' ? (
                <>
                  New here?{' '}
                  <button type="button" onClick={() => { setAuthMode('register'); setAuthError(''); }} className="font-medium text-gray-900 hover:underline">
                    Create an account
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button type="button" onClick={() => { setAuthMode('login'); setAuthError(''); }} className="font-medium text-gray-900 hover:underline">
                    Sign in
                  </button>
                </>
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contact;